import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { 
  MapPin, Mail, Lock, User, ArrowLeft, Loader2, Eye, EyeOff,
  Shield, CheckCircle, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAPTAG_LOGO = "https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG";

const PRIVACY_POLICY = {
  title: "Politika e Privatësisë - TAPTAG",
  lastUpdated: "Mars 2024",
  sections: [
    {
      title: "1. Të Dhënat që Mbledhim",
      content: "TAPTAG mbledh vetëm të dhënat e nevojshme për funksionimin e shërbimit: emrin, email-in dhe numrin e telefonit tuaj. Gjatë skanimit të një stikeri, mund të mbledhim lokacionin tuaj vetëm me pëlqimin tuaj të shprehur."
    },
    {
      title: "2. Si Përdoren të Dhënat",
      content: "Të dhënat tuaja përdoren ekskluzivisht për: identifikimin e objekteve të humbura, komunikimin midis gjetësit dhe pronarit, dhe funksionimin e sistemit të shpërblimeve. Nuk shesim dhe nuk ndajmë të dhënat tuaja me palë të treta."
    },
    {
      title: "3. Siguria e të Dhënave",
      content: "Të gjitha të dhënat ruhen në mënyrë të sigurt dhe të enkriptuar. Vetëm pronari i objektit ka akses në informacionin e kontaktit të gjetësit. Admini monitoron sistemin vetëm për qëllime sigurie dhe nuk ka akses në komunikimet private."
    },
    {
      title: "4. Të Drejtat Tuaja",
      content: "Keni të drejtë të: shikoni të dhënat tuaja, kërkoni korrigjimin ose fshirjen e tyre, tërhiqni pëlqimin në çdo kohë. Për çdo kërkesë, kontaktoni: privacy@taptag.al"
    },
    {
      title: "5. Ruajtja e të Dhënave",
      content: "Të dhënat ruhen për aq kohë sa llogaria juaj është aktive. Pas fshirjes së llogarisë, të dhënat fshihen brenda 30 ditëve."
    }
  ]
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Plotëso të gjitha fushat');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Fjalëkalimet nuk përputhen');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë së paku 6 karaktere');
      return;
    }

    if (!acceptedPrivacy) {
      toast.error('Duhet të pranosh Politikën e Privatësisë');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        accepted_privacy: true
      });
      
      login(response.data.user, response.data.access_token);
      toast.success('Llogaria u krijua me sukses!');
      navigate(redirectTo || '/llogaria');
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Gabim në krijimin e llogarisë');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="floating-orb w-96 h-96 bg-violet-500/20 -top-48 -right-48 animate-float" />
        <div className="floating-orb w-64 h-64 bg-cyan-500/20 bottom-20 -left-32 animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute inset-0 grid-pattern opacity-30" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Kthehu
        </Link>

        {/* Card */}
        <Card className="bg-white border-slate-200 shadow-xl">
          <CardContent className="p-8">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <img 
                src={TAPTAG_LOGO}
                alt="TAPTAG Logo"
                className="w-14 h-14 rounded-full object-cover shadow-lg"
              />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">
              Krijo Llogari
            </h1>
            <p className="text-slate-500 text-center mb-6">
              Regjistrohu për të mbrojtur objektet e tua
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Emër Mbiemër</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Emër Mbiemër"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-11 h-12 border-slate-300"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-11 h-12 border-slate-300"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Fjalëkalimi</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-11 pr-11 h-12 border-slate-300"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">Konfirmo Fjalëkalimin</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-11 pr-11 h-12 border-slate-300"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Privacy Policy Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <Checkbox
                  id="privacy"
                  checked={acceptedPrivacy}
                  onCheckedChange={setAcceptedPrivacy}
                  className="mt-0.5"
                  data-testid="privacy-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="privacy" className="text-sm text-slate-700 cursor-pointer">
                    Pranoj{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-violet-600 hover:text-violet-700 underline font-medium"
                    >
                      Politikën e Privatësisë
                    </button>
                    {' '}dhe konfirmoj që të dhënat e mia do të përpunohen në përputhje me të.
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !acceptedPrivacy}
                className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg disabled:opacity-50"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Duke krijuar...
                  </>
                ) : (
                  'Krijo Llogarinë'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <span className="text-slate-500">Ke llogari? </span>
              <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium">
                Hyr këtu
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden animate-fade-in-up">
            {/* Header */}
            <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-violet-600 to-purple-600">
              <div className="flex items-center gap-3 text-white">
                <Shield className="w-6 h-6" />
                <div>
                  <h2 className="text-xl font-bold">{PRIVACY_POLICY.title}</h2>
                  <p className="text-white/80 text-sm">Përditësuar: {PRIVACY_POLICY.lastUpdated}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowPrivacyModal(false)} 
                className="p-2 hover:bg-white/20 rounded-full text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-6">
                {PRIVACY_POLICY.sections.map((section, index) => (
                  <div key={index}>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{section.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{section.content}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Të dhënat tuaja janë të sigurta</span>
              </div>
              <Button
                onClick={() => {
                  setAcceptedPrivacy(true);
                  setShowPrivacyModal(false);
                }}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                Pranoj dhe Mbyll
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
